/**
 * URLs públicas OAuth VIDaaS (client_id no browser é aceitável; segredo só na Edge).
 * @see https://valid-sa.atlassian.net/wiki/spaces/PDD/pages/958365697/
 */

import {
  parseVidaasSessionMode,
  vidaasAuthorizeForMode,
  type VidaasSessionMode,
} from '@/shared/lib/digitalSignaturePrefs'

export const SYNAP_VIDAAS_OAUTH_STORAGE_KEY = 'synap_vidaas_oauth_result'

export const vidaasPublicApiBase = (): string => {
  const v = import.meta.env.VITE_VIDAAS_API_BASE_URL as string | undefined
  return (v?.trim() || 'https://certificado.vidaas.com.br').replace(/\/$/, '')
}

export const buildVidaasAuthorizeUrl = (opts: {
  clientId: string
  codeChallenge: string
  redirectUri: string
  state: string
  scope?: string
  loginHintDigits?: string
  mode: 'qrcode' | 'push'
  /** Padrão: a cada documento (`sign`, 15 min). `twelve_hours` pede `signature_session`. */
  sessionMode?: VidaasSessionMode
}): string => {
  const base = `${vidaasPublicApiBase()}/v0/oauth/authorize`
  const redirect = opts.mode === 'push' ? 'push://' : opts.redirectUri.trim()
  const authorize = vidaasAuthorizeForMode(parseVidaasSessionMode(opts.sessionMode))
  const params = new URLSearchParams({
    client_id: opts.clientId.trim(),
    code_challenge: opts.codeChallenge.trim(),
    code_challenge_method: 'S256',
    response_type: 'code',
    scope: (opts.scope ?? authorize.scope).trim(),
    redirect_uri: redirect,
    state: opts.state,
    lifetime: String(authorize.lifetimeSec),
  })
  const hint = opts.loginHintDigits?.replace(/\D/g, '') ?? ''
  if (hint.length === 11) {
    params.set('login_hint', hint)
  }
  return `${base}?${params.toString()}`
}

/** Traduz o resultado do preflight (vidaas-authorize-check) em mensagem PT acionável. */
export const describeVidaasAuthorizeError = (
  result: { code?: number; messageKey?: string; message?: string },
  redirectUri: string,
): string => {
  if (result.messageKey === 'application.uri.invalid' || result.code === 108) {
    return (
      `O VIDaaS recusou o endereço de retorno (redirect_uri) “${redirectUri}”: ele não está ` +
      'registrado na aplicação. Peça ao administrador para registrar esse endereço no portal VIDaaS ' +
      '(Valid) ou use “Notificação no app”.'
    )
  }
  if (result.message?.trim() || result.messageKey?.trim()) {
    const detail = result.message?.trim() || result.messageKey?.trim()
    const key = result.messageKey?.trim() && result.message?.trim() ? ` (${result.messageKey.trim()})` : ''
    return `O VIDaaS recusou a autorização: ${detail}${key}.`
  }
  return 'Não foi possível iniciar a autorização no navegador do VIDaaS. Tente novamente ou use “Notificação no app”.'
}

export const defaultVidaasRedirectPath = '/auth/vidaas/callback'

export const buildDefaultVidaasRedirectUri = (): string => {
  if (typeof window === 'undefined') return ''
  return `${window.location.origin}${defaultVidaasRedirectPath}`
}
